import { useRouter } from '@abyss/web/hooks/useRouter';
import { tokenizer } from '@abyss/web/tools/tokenizer';
import { Heading } from '@abyss/web/ui/Heading';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Layout } from '@abyss/web/ui/Layout';
import { Text } from '@abyss/web/ui/Text';
import { Fragment, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { adobeFuncErrorEvent } from '../../common/AdobeTagging/adobeFuncErrorEvent';
import { isGuestFlow } from '../../utils/error.utils';
import { PortalIdentifier } from '../../common/Portal';
import { PSXHeader } from '../../common/PSXHeader';
import { StillNeedHelp } from '../../common/StillNeedHelp';
import { PortalContext } from '../../context/PortalContext';
import { useAdobePageTrackEvent } from '../../hooks/adobeHook/useAdobePageTrackEvent';
import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import { Icon } from './Icon';
import { IconContainer, PageContainer, PageContent } from './Page408.style';

export const Page408 = () => {
  const { t } = useTranslation();
  const refreshPage = () => {
    window.location.reload();
  };
  const breadcrumbs = [{ title: t('ERROR_PAGE.408.title'), href: '' }];
  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    is404: true,
    pageName: 'error 408',
  });

  const { getRouteParams } = useRouter();
  const { token } = getRouteParams();
  const isGuest = isGuestFlow();
  const tokenData = tokenizer.parse(token) || {};
  const { message, code, source, uiSection } = tokenData;

  const { portalData } = useContext(PortalContext);
  const isGeha = portalData?.portalIdentifier === PortalIdentifier.GEHA;
  const isGEHAV2 = shouldUseGehaV2Styling();
  const isGehaTheme = isGEHAV2 || isGeha;

  useEffect(() => {
    adobeFuncErrorEvent({
      message,
      source,
      code: code ?? '408',
      uiSection,
    });
    adobePageTrackEvent();
  }, []);

  return (
    <Fragment>
      <PSXHeader
        breadcrumbs={breadcrumbs}
        dataTestId="error-408-search-form"
        showChips={false}
        showChoosePCPHeader={false}
        showDemoHeader={false}
        showMemberSelection={false}
      />
      <PageContainer>
        {!isGehaTheme && (
          <IconContainer>
            <Icon />
          </IconContainer>
        )}
        <PageContent>
          <Text
            color="$errorTextColor"
            css={{
              'abyss-text-root': {
                marginTop: isGehaTheme ? '130px' : '40px',
                marginBottom: '4px',
                '@screen < $sm': {
                  marginBottom: '29px',
                  marginTop: 'unset',
                },
              },
            }}
            fontWeight="$bold"
            size="$xs"
          >
            {t('ERROR_PAGE.408.type')}
          </Text>
          <Heading
            css={{
              marginBottom: '4px',
              '@screen < $sm': {
                marginBottom: '16px',
              },
            }}
          >
            {t('ERROR_PAGE.408.heading')}
          </Heading>
          <Heading
            css={{
              marginBottom: '12px',
              '@screen < $sm': {
                marginBottom: '19px',
              },
            }}
            offset={5}
          >
            {t('ERROR_PAGE.408.subHeading')}
          </Heading>
          <Text
            css={{
              'abyss-text-root': {
                marginBottom: '30px',
                '@screen < $sm': {
                  marginBottom: '9px',
                  maxWidth: '190px',
                },
              },
            }}
            size="$sm"
          >
            {t('ERROR_PAGE.408.body1')}
          </Text>
          <Layout.Group
            css={{
              cursor: 'pointer',
            }}
            data-auto-testid="refresh"
            data-testid="refresh"
            onClick={refreshPage}
            role="button"
          >
            <Text color="$refreshIconColor" fontWeight="$bold" size="$sm">
              {t('ERROR_PAGE.408.Refresh')}
            </Text>
            <IconMaterial
              color="$refreshIconColor"
              icon="refresh"
              size="12px"
            />
          </Layout.Group>
        </PageContent>
      </PageContainer>
      {!isGuest && <StillNeedHelp />}
    </Fragment>
  );
};
